/* eslint-disable complexity */
import { get, isEmpty, includes } from 'lodash-es'
import { actionTypes as types } from '../constants'
import { firstDefined } from 'utils/utils'
import {
  getInstrumentThatAttemptedToResolve,
  getContingencyTypeFromFundingSource,
  getWalletRedirectStatus,
} from 'utils/card'
import { getFundingQueryDimension } from './funding.selectors'
import { FUNDING_SOURCE, RESOLVED_CONTINGENCY_TYPE, WALLET_REDIRECT } from 'universal'

const initialState = {
  // These three state variables control loading states in ModalPage's renderModalShell and renderBlockingSpinner.
  // isFetching is also used to determine whether data is available.
  isFetching: false,
  isFetchUIBlocking: true,
  noContent: false,
  // Display error page
  fetchError: null,
  // Display success page
  fetchSuccessAction: '',
  // Display retry request page
  fetchRetry: null,
  // Card ID to Funding map
  funding: {},
}

const fetchingState = (action) => ({
  isFetching: true,
  isFetchUIBlocking: (firstDefined as any)(action.isFetchUIBlocking, true),
  noContent: Boolean(action.isFetchOnMount),
  fetchError: null,
  fetchSuccessAction: '',
})

const successState = (actionType) => ({
  isFetching: false,
  isFetchUIBlocking: true, // set this to its base state even though it's meaningless when isFetching is false
  noContent: false,
  fetchRetry: null,
  fetchSuccessAction: actionType,
})

/**
 * Returns the updated array of funding sources
 *
 * Updated based on the response, resolveContingencyResponse, obtained from resolve-contingency api
 *
 * 'status' & 'contingencyDetails' from resolveContingencyResponse takes precedence over the existing data from fetchFundingDetails
 * These fields will be overwritten because:
 *    - they represent current state of the
 *    - they determine how we should handle user's actions for the
 * selected funding source.
 *
 * @param {array} fundingSources
 * @param {object} action
 * @returns {array}
 */
const updateFundingSourcesOnResolveContingencyFailure = (fundingSources, action) => {
  const { fundingSourceId, status, contingencyDetails } = action.resolveContingencyResponse || {}
  return fundingSources.map((source) => {
    if (source.id !== fundingSourceId) {
      return source
    }
    return {
      ...source,
      status,
      contingencyDetails,
    }
  })
}

/**
 * Returns the updated array of funding sources
 *
 * Updated based on query parameters obtained from the contingency resolver flow
 *    e.g. GDS update funding source success flow - updated funding source ID and success ID will be returned
 *
 * If the selected funding source returns from wallet with a:
 *    1) success - the status of the funding source will be overwritten with an ELIGIBLE status. (with success query parameter)
 *       This enables the user to proceed to the next action
 *       e.g. GDS - user will be able to create card after successfully updating the funding source
 *    2) failure - the status of the funding source will be overwritten with a DENIED status. (without success query parameter)
 *       This prevents the user to proceed to the next action
 *       e.g. GDS - user will not be able to create card with the selected funding source
 *
 * @param {array} fundingSources
 * @returns {array}
 */
const updateFundingSourcesOnFundingFetch = (fundingSources) => {
  const walletRedirectStatus = getWalletRedirectStatus()

  if (isEmpty(fundingSources) || !walletRedirectStatus) {
    return fundingSources
  }

  const { ELIGIBLE, DENIED } = FUNDING_SOURCE.STATUS
  const listOfContingenciesThatCanBeResolvedOrOverridenViaQueryParams = [
    RESOLVED_CONTINGENCY_TYPE.INSTRUMENT_PREAUTHORIZATION,
    RESOLVED_CONTINGENCY_TYPE.UPDATE_INSTRUMENT,
    RESOLVED_CONTINGENCY_TYPE.OPEN_BANKING_CONSENT_REQUIRED,
  ]
  const contingencyOnFiThatUnderwentAttemptedContingencyResolutionViaRedirect = getContingencyTypeFromFundingSource(
    fundingSources,
  )

  // overwrite the FI status received from service with the fresher FI status received from the FI contingency resolver flow
  let contingencyResolvedViaRedirect, status
  if (
    includes(
      listOfContingenciesThatCanBeResolvedOrOverridenViaQueryParams,
      contingencyOnFiThatUnderwentAttemptedContingencyResolutionViaRedirect,
    )
  ) {
    const { SUCCESS, FAILURE } = WALLET_REDIRECT.STATUS
    const STATUS_MAP = {
      [SUCCESS]: ELIGIBLE,
      [FAILURE]: DENIED,
    }

    contingencyResolvedViaRedirect = walletRedirectStatus === SUCCESS
    status = STATUS_MAP[walletRedirectStatus]
  }

  const resolvedContingencyFiId = getInstrumentThatAttemptedToResolve()
  return (fundingSources || []).map((source) => {
    if (source.id === resolvedContingencyFiId && !!status) {
      return {
        ...source,
        status,
        contingencyResolvedViaRedirect,
      }
    }
    return source
  })
}

function card(state = initialState, action: any = {}) {
  switch (action.type) {
    case types.FETCH_FUNDING_SUMMARY:
    case types.FETCH_FUNDING_DETAILS:
    case types.SET_FUNDING:
    case types.REMOVE_FUNDING:
      return {
        ...state,
        ...fetchingState(action),
      }

    case types.FETCH_FUNDING_SUMMARY_SUCCESS:
    case types.SET_FUNDING_SUCCESS:
    case types.REMOVE_FUNDING_SUCCESS: {
      const fundingQueryDimension = getFundingQueryDimension(action)
      const fundingQueryDimensionObject = fundingQueryDimension
        ? {
            [fundingQueryDimension]: {
              ...get(state, ['funding', fundingQueryDimension], {}),
              ...action.funding,
            },
          }
        : {}
      return {
        ...state,
        ...successState(action.type),
        funding: {
          ...state.funding,
          ...fundingQueryDimensionObject,
        },
      }
    }

    case types.FETCH_FUNDING_DETAILS_SUCCESS: {
      const fundingQueryDimension = getFundingQueryDimension(action)
      const fundingQueryDimensionObject = fundingQueryDimension
        ? {
            [fundingQueryDimension]: {
              ...get(state, ['funding', fundingQueryDimension], {}),
              ...action.funding,
              availableFundingOptions: {
                ...get(action, `funding.availableFundingOptions`, {}),
                fundingSources: updateFundingSourcesOnFundingFetch(
                  get(action, ['funding', 'availableFundingOptions', 'fundingSources'], []),
                ),
              },
            },
          }
        : {}

      return {
        ...state,
        ...successState(action.type),
        funding: {
          ...state.funding,
          ...fundingQueryDimensionObject,
        },
      }
    }

    case types.RESOLVE_CONTINGENCY_FAILURE: {
      const fundingQueryDimension = getFundingQueryDimension(action)
      const fundingQueryDimensionObject = fundingQueryDimension
        ? {
            [fundingQueryDimension]: {
              ...get(state, ['funding', fundingQueryDimension], {}),
              availableFundingOptions: {
                fundingSources: updateFundingSourcesOnResolveContingencyFailure(
                  get(
                    state,
                    ['funding', fundingQueryDimension, 'availableFundingOptions', 'fundingSources'],
                    [],
                  ),
                  action,
                ),
              },
            },
          }
        : {}

      return {
        ...state,
        ...successState(action.type),
        funding: {
          ...state.funding,
          ...fundingQueryDimensionObject,
        },
      }
    }

    /**
     * creates {fundingSlice}.funding.CONSUMER_VIRTUAL_DEBIT_CARD: { ...fundingDetails }
     */
    case types.APPLY_CARD_SUCCESS:
      const productNameFromApplyResult = get(
        action,
        'applicationResult.applyForCreate.cardAttributes.productName',
      )
      return {
        ...state,
        funding: {
          ...state.funding,
          [productNameFromApplyResult]: {
            ...get(action, 'applicationResult.fundingDetails', {}),
            availableFundingOptions: {
              ...get(action, 'applicationResult.fundingDetails.availableFundingOptions', {}),
              fundingSources: updateFundingSourcesOnFundingFetch(
                get(
                  action,
                  [
                    'applicationResult',
                    'fundingDetails',
                    'availableFundingOptions',
                    'fundingSources',
                  ],
                  [],
                ),
              ),
            },
          },
        },
      }

    // change funding key from productName to cardId now that a cardId exists!
    // TODO also inject userPreferences and fundingStatus here to pre-populate Summary's FundingPanel
    case types.CREATE_CARD_SUCCESS:
      const { productName: productNameFromCreateResult, cardId } = action.card || {}
      const {
        [productNameFromCreateResult]: fundingForThisProduct,
        ...fundingWithoutThisProduct
      } = state.funding as any
      return {
        ...state,
        funding: {
          ...fundingWithoutThisProduct,
          [cardId]: fundingForThisProduct,
        },
      }

    case types.FETCH_FUNDING_DETAILS_FAILURE:
    case types.SET_FUNDING_FAILURE:
    case types.REMOVE_FUNDING_FAILURE:
      return {
        ...state,
        isFetching: false,
        noContent: false,
        fetchError: action.fetchError,
        fetchRetry: action.fetchRetry,
      }

    // the purpose of this is to remove ONLY blocking spinners/shimmers/errors/retries
    // TODO rename to indicate intent to something like "reset fetch-related loaders and errors"
    case types.RESET_CARD_REQUEST:
      return {
        ...state,
        isFetching: false,
        noContent: false,
        fetchSuccessAction: '',
        fetchError: null,
        fetchRetry: null,
      }

    default:
      return state
  }
}

export default card
